"use client";

import React, { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { appsMainMenuHiddenClass, SHOW_APPS_MAIN_MENU } from "./navConfig";
import { WatchOurStoryLink } from "./WatchOurStoryLink";
import { deriveLocaleFromPath, localizeHref } from "@/lib/i18n";
import { getMessages } from "@/lib/messages";

/** When true, same-page links use # (e.g. #services). When false, use /# for cross-page anchors. */
export function MobileNav({ homePage = false }: { homePage?: boolean }) {
  const [open, setOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  const [hipaaOpen, setHipaaOpen] = useState(false);
  const pathname = usePathname() || "/";
  const ctx = deriveLocaleFromPath(pathname);
  const m = getMessages(ctx.locale);

  const openAppsDrawer = () => {
    setAppsOpen(true);
    setOpen(true);
  };

  return (
    <>
      <nav
        className="fixed left-4 right-4 bottom-4 z-30 flex items-center justify-around gap-1 rounded-2xl border border-white px-1 py-2.5 text-white md:hidden"
        style={{
          background: "rgba(30, 42, 58, 0.45)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
        }}
        aria-label={m.nav.navigation}
      >
        <button
          type="button"
          onClick={openAppsDrawer}
          className={`rounded px-1.5 py-2 text-xs font-medium transition-colors hover:bg-[#77a4d7] active:bg-[#77a4d7] ${appsMainMenuHiddenClass()}`.trim()}
          aria-expanded={open && appsOpen}
          aria-controls="mobile-nav-drawer"
          aria-hidden={SHOW_APPS_MAIN_MENU ? undefined : true}
          tabIndex={SHOW_APPS_MAIN_MENU ? undefined : -1}
        >
          {m.nav.apps}
        </button>
        <Link href={localizeHref("/web-support", ctx)} className="rounded px-1.5 py-2 text-xs font-medium transition-colors hover:bg-[#77a4d7] active:bg-[#77a4d7]">
          {m.nav.support}
        </Link>
        <Link href={localizeHref("/hosting", ctx)} className="rounded px-1.5 py-2 text-xs font-medium transition-colors hover:bg-[#77a4d7] active:bg-[#77a4d7]">
          {m.nav.hosting}
        </Link>
        <Link href={localizeHref("/help", ctx)} className="rounded px-1.5 py-2 text-xs font-medium transition-colors hover:bg-[#77a4d7] active:bg-[#77a4d7]">
          {m.nav.help}
        </Link>
        <a
          href="https://panel.cloudaxe.io"
          target="_blank"
          rel="noopener noreferrer"
          className="rounded px-1.5 py-2 text-xs font-medium transition-colors hover:bg-[#77a4d7] active:bg-[#77a4d7]"
        >
          {m.nav.clients}
        </a>
      </nav>

      <div
        id="mobile-nav-drawer"
        className="fixed inset-4 z-40 flex max-h-[calc(100vh-2rem)] flex-col rounded-2xl border border-white shadow-2xl transition-transform duration-300 ease-out md:hidden"
        style={{
          transform: open ? "translateY(0)" : "translateY(100%)",
          visibility: open ? "visible" : "hidden",
          pointerEvents: open ? "auto" : "none",
          background: "rgba(30, 42, 58, 0.45)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
        }}
        aria-hidden={!open}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-white/20 px-4 py-3">
          <span className="text-sm font-semibold text-white">{m.nav.navigation}</span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded p-2 text-white/90 hover:bg-white/20"
            aria-label={m.nav.closeMenu}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {homePage ? (
            <WatchOurStoryLink className="block border-b border-white/10 px-4 py-3 text-sm font-medium text-white hover:bg-white/10" />
          ) : null}

          <div
            className={`border-b border-white/10 ${appsMainMenuHiddenClass()}`.trim()}
            aria-hidden={SHOW_APPS_MAIN_MENU ? undefined : true}
          >
            <button
              type="button"
              onClick={() => setAppsOpen((o) => !o)}
              className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-white hover:bg-white/10"
              aria-expanded={appsOpen}
            >
              {m.nav.apps}
              <svg
                className={`h-4 w-4 transition-transform ${appsOpen ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {appsOpen && (
              <div className="border-t border-white/10 bg-[#1e4574]/80 pb-2">
                <div className="border-b border-white/5">
                  <button
                    type="button"
                    onClick={() => setHipaaOpen((o) => !o)}
                    className="flex w-full items-center justify-between px-6 py-2.5 text-left text-sm text-white/95 hover:bg-white/10"
                    aria-expanded={hipaaOpen}
                  >
                    {m.nav.hipaaForms}
                    <svg
                      className={`h-4 w-4 transition-transform ${hipaaOpen ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {hipaaOpen && (
                    <div className="bg-[#0f2847]/80 pb-2">
                      <Link href="/apps/hipaa-compliant-forms" className="block px-8 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setOpen(false)}>
                        {m.nav.overview}
                      </Link>
                      <Link href="/apps/hipaa-compliant-forms/register" className="block px-8 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setOpen(false)}>
                        {m.nav.getStarted}
                      </Link>
                      <Link href="/apps/hipaa-compliant-forms/pricing" className="block px-8 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setOpen(false)}>
                        {m.nav.pricing}
                      </Link>
                      <Link href="/apps/hipaa-compliant-forms/developers" className="block px-8 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setOpen(false)}>
                        {m.nav.developers}
                      </Link>
                      <Link href="/apps/hipaa-compliant-forms/developers/api" className="block px-8 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setOpen(false)}>
                        {m.nav.api}
                      </Link>
                      <Link href="/apps/hipaa-compliant-forms/integrations/wordpress" className="block px-8 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setOpen(false)}>
                        {m.nav.wordpress}
                      </Link>
                      <Link href="/apps/hipaa-compliant-forms/integrations/joomla" className="block px-8 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setOpen(false)}>
                        {m.nav.joomla}
                      </Link>
                      <Link href="/apps/hipaa-compliant-forms/faq" className="block px-8 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setOpen(false)}>
                        {m.nav.faq}
                      </Link>
                      <Link href="/apps/hipaa-compliant-forms/security" className="block px-8 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setOpen(false)}>
                        {m.nav.security}
                      </Link>
                    </div>
                  )}
                </div>
                <Link
                  href="/apps/custom-apps"
                  className="block px-6 py-2.5 text-sm text-white/95 hover:bg-white/10"
                  onClick={() => setOpen(false)}
                >
                  {m.nav.customApps}
                </Link>
              </div>
            )}
          </div>

          <Link
            href={localizeHref("/web-support", ctx)}
            className="block border-b border-white/10 px-4 py-3 text-sm font-medium text-white hover:bg-white/10"
            onClick={() => setOpen(false)}
          >
            {m.nav.webSupport}
          </Link>
          <Link
            href={localizeHref("/hosting", ctx)}
            className="block border-b border-white/10 px-4 py-3 text-sm font-medium text-white hover:bg-white/10"
            onClick={() => setOpen(false)}
          >
            {m.nav.hosting}
          </Link>
          <Link
            href={localizeHref("/help/faqs", ctx)}
            className="block border-b border-white/10 px-4 py-3 text-sm font-medium text-white hover:bg-white/10"
            onClick={() => setOpen(false)}
          >
            {m.nav.faqs}
          </Link>
          <Link
            href={localizeHref("/help", ctx)}
            className="block border-b border-white/10 px-4 py-3 text-sm font-medium text-white hover:bg-white/10"
            onClick={() => setOpen(false)}
          >
            {m.nav.help}
          </Link>
          <a
            href="https://panel.cloudaxe.io"
            target="_blank"
            rel="noopener noreferrer"
            className="block border-b border-white/10 px-4 py-3 text-sm font-medium text-white hover:bg-white/10"
            onClick={() => setOpen(false)}
          >
            {m.nav.clients}
          </a>
        </div>
      </div>

      {open && (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          aria-label={m.nav.closeMenu}
          onClick={() => setOpen(false)}
        />
      )}
    </>
  );
}
